// src/components/ui/AssignToClientModal.js

import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Divider,
} from "@mui/material";
import ButtonComponent from "./ButtonComponent";
import TextInputComponent from "./TextInputComponent";

const AssignToClientModal = ({
  open,
  onClose,
  clients,
  excludeId,
  onSelect,
  title,
  isSubmitting,
  onCreateNewClient,
  newlyCreatedClient,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  suggestedClientName,
}) => {
  const [search, setSearch] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());

  // Notification controls
  const [sendInvite, setSendInvite] = useState(true);
  const [channel, setChannel] = useState("email"); // 'email' | 'sms'

  // Reset when opened
  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedClientIds(new Set());
      setSendInvite(true);
      setChannel("email");
    }
  }, [open]);

  // Evaluate the suggestion behind the scenes (unobtrusive)
  const suggestedMatch = useMemo(() => {
    if (!suggestedClientName || suggestedClientName === "Annotated Document") return null;
    
    const lowerSuggestion = suggestedClientName.toLowerCase();
    const list = clients || [];
    
    // Look for an exact match first
    const exact = list.find(c => c.fullName?.toLowerCase() === lowerSuggestion);
    if (exact) return { type: 'match', client: exact };
    
    // If no match, prep the "create" prompt
    return { type: 'create', name: suggestedClientName };
  }, [suggestedClientName, clients]);

  const filteredClients = useMemo(() => {
    const lower = search.toLowerCase();
    const list = clients || [];
    return list
      .filter((client) => {
        const nameMatch = (client.fullName || "").toLowerCase().includes(lower);
        const emailMatch = (client.email || "").toLowerCase().includes(lower);
        return client.id !== excludeId && (nameMatch || emailMatch);
      })
      .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
  }, [clients, search, excludeId]);

  // If a new client is created, auto-select them
  useEffect(() => {
    if (newlyCreatedClient && newlyCreatedClient.id) {
      setSelectedClientIds(new Set([newlyCreatedClient.id]));
      setSearch("");
    }
  }, [newlyCreatedClient]);

  const handleToggle = (clientId) => {
    if (isSubmitting) return;
    const next = new Set(selectedClientIds);
    if (next.has(clientId)) {
      next.delete(clientId);
    } else {
      next.add(clientId);
    }
    setSelectedClientIds(next);
  };

  const handleConfirm = () => {
    const list = clients || [];
    const selectedClients = list.filter((client) =>
      selectedClientIds.has(client.id)
    );

    const notifyOptions = {
      sendNotification: sendInvite,
      channel,
    };

    onSelect(selectedClients, notifyOptions);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiDialog-paper": { height: "80vh", maxHeight: 600 } }}
    >
      <DialogTitle>{title || "Assign to Clients"}</DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", p: 0 }}
      >
        {/* Top bar: search + create new */}
        <Box
          sx={{
            p: "16px 24px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <TextInputComponent
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            readOnly={isSubmitting}
            sx={{
              flexGrow: 1,
              maxWidth: "calc(100% - 150px)",
            }}
          />

          <ButtonComponent
            label="+ New Client"
            onClick={() => {
              onClose();
              onCreateNewClient();
            }}
            type="secondary"
            size="medium"
            disabled={isSubmitting}
            sx={{
              minWidth: 140,
              whiteSpace: "nowrap",
            }}
          />
        </Box>

        {/* Unobtrusive Smart Suggestion Banner */}
        {!search && suggestedMatch && (
          <Box 
            sx={{ 
              mb: 1, mx: 3, p: 1.5, 
              backgroundColor: "primary.50", 
              borderRadius: 1, 
              border: "1px solid", 
              borderColor: "primary.200", 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}
          >
            {suggestedMatch.type === 'match' ? (
              <>
                <Typography variant="body2" sx={{ color: "primary.900" }}>
                  <strong>Suggested Match:</strong> {suggestedMatch.client.fullName}
                </Typography>
                <ButtonComponent 
                  label={selectedClientIds.has(suggestedMatch.client.id) ? "Selected" : "Select"} 
                  type={selectedClientIds.has(suggestedMatch.client.id) ? "secondary" : "primary"} 
                  size="small" 
                  onClick={() => handleToggle(suggestedMatch.client.id)}
                />
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: "primary.900" }}>
                  No match found for <strong>"{suggestedMatch.name}"</strong>
                </Typography>
                <ButtonComponent 
                  label="Create Client" 
                  type="primary" 
                  size="small" 
                  onClick={() => {
                    onClose();
                    onCreateNewClient();
                  }}
                />
              </>
            )}
          </Box>
        )}

        {/* Client list */}
        {filteredClients.length === 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
              pb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              No matching clients found.
            </Typography>
          </Box>
        ) : (
          <List sx={{ flexGrow: 1, overflow: "auto", px: 3, pb: 1 }}>
            {filteredClients.map((client) => {
              const labelId = `checkbox-list-label-${client.id}`;
              const isSuggested = suggestedMatch?.type === 'match' && suggestedMatch.client.id === client.id;
              
              return (
                <ListItem
                  key={client.id}
                  disablePadding
                  button
                  onClick={() => handleToggle(client.id)}
                  disabled={isSubmitting}
                  sx={{
                    py: 0.75,
                    backgroundColor: isSuggested && !search ? "action.hover" : "transparent",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <ListItemText
                    id={labelId}
                    primary={client.fullName || "Unnamed Client"}
                    secondary={client.email || client.phone || ""}
                    primaryTypographyProps={{ variant: "body2", fontWeight: isSuggested ? 600 : 400 }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                    }}
                  />
                  <Checkbox
                    edge="end"
                    onChange={() => handleToggle(client.id)}
                    checked={selectedClientIds.has(client.id)}
                    inputProps={{ "aria-labelledby": labelId }}
                    disabled={isSubmitting}
                  />
                </ListItem>
              );
            })}

          {hasMore && (
            <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
              <ButtonComponent
                label={isLoadingMore ? "Loading..." : "Load more clients"}
                type="secondary"
                size="small"
                disabled={isSubmitting || isLoadingMore || typeof onLoadMore !== "function"}
                isLoading={isLoadingMore}
                onClick={() => onLoadMore && onLoadMore()}
              />
            </Box>
            )}
          </List>
          )}

        {/* Notification section */}
        <Box sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Client notification
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1.5, display: "block" }}
          >
            Optionally notify selected clients that new documents are available
            in their portal.
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                  disabled={isSubmitting}
                />
              }
              label="Send Client Portal notification"
            />
            <TextField
              select
              size="small"
              label="Channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              sx={{ width: 180 }}
              disabled={!sendInvite || isSubmitting}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
            </TextField>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <ButtonComponent
          label="Cancel"
          type="secondary"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <ButtonComponent
          label={`Assign Selected (${selectedClientIds.size})`}
          type="primary"
          onClick={handleConfirm}
          isLoading={isSubmitting}
          disabled={selectedClientIds.size === 0 || isSubmitting}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AssignToClientModal;